import { ETelemetryTopOf } from 'multirpc-sdk';
import { useMemo } from 'react';

import { REFETCH_STATS_INTERVAL } from 'modules/stats/const';
import { getStatsChainID } from 'modules/chains/utils/getStatsChainID';
import { isSSG } from 'modules/common/utils/isSSG';
import {
  selectMappedPublicRequestsChartData,
  selectPublicCountriesCountsMap,
  selectPublicTotalRequests,
} from 'modules/stats/store/public/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useChainSelectors } from 'modules/chains/components/ChainSelectorsProvider';
import { useFetchPublicStatsQuery } from 'modules/stats/actions/fetchPublicStats';
import { usePublicStatsParams } from 'modules/chains/hooks/usePublicStatsParams';
import { useTimeframe } from 'modules/chains/components/TimeframeProvider';

import { StatsContextValue } from '../context';

const metrics = [ETelemetryTopOf.COUNTRY];

export const usePublicStats = () => {
  const { isLoggedIn } = useAuth();
  const { timeframe } = useTimeframe();

  const {
    chain,
    chainSubType,
    chainType,
    consensus,
    group,
    isConsensusEnabled,
  } = useChainSelectors();

  const chainId = getStatsChainID({
    chainSubType,
    chainType,
    consensus,
    group,
    isConsensusEnabled,
    publicChain: chain,
  });

  const { publicStatsParams } = usePublicStatsParams({
    chainId,
    metrics,
    skipFetching: isSSG(),
    timeframe,
  });

  const { isFetching: loading } = useFetchPublicStatsQuery(publicStatsParams, {
    refetchOnMountOrArgChange: REFETCH_STATS_INTERVAL,
    skip: isSSG() || isLoggedIn,
  });

  const countries = useAppSelector(state =>
    selectPublicCountriesCountsMap(state, publicStatsParams),
  );
  const requestsChartData = useAppSelector(state =>
    selectMappedPublicRequestsChartData(state, publicStatsParams),
  );
  const totalRequests = useAppSelector(state =>
    selectPublicTotalRequests(state, publicStatsParams),
  );

  return useMemo(
    (): StatsContextValue => ({
      countries,
      countriesByTraffic: {},
      loading,
      requestsChartData,
      totalBytes: 0,
      totalRequests,
      trafficChartData: [],
    }),
    [countries, loading, requestsChartData, totalRequests],
  );
};
